import { AfterViewInit, Component, ElementRef, HostListener, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PhotoFilter } from '../../../../photos/photo-filter';

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'app-photo-lightbox',
  templateUrl: './photo-lightbox.component.html',
  styleUrls: ['./photo-lightbox.component.less'],
})
export class PhotoLightboxComponent implements AfterViewInit {
  filter: PhotoFilter;
  photoURL: string;
  loading = false;
  private zoomScale = 1;
  private translatePoint: Point = { x: 0, y: 0 };
  private pointerEventCache: PointerEvent[] = [];
  private pinchDiff = 0;
  @ViewChild('zoomContainer') zoomContainer: ElementRef<HTMLDivElement>;

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.filter) {
      if (event.key === 'ArrowLeft') {
        this.incrementPhoto(-1);
      } else if (event.key === 'ArrowRight') {
        this.incrementPhoto(1);
      }
    }
  }

  constructor(public dialogRef: MatDialogRef<PhotoLightboxComponent>, @Inject(MAT_DIALOG_DATA) public data: any) {
    this.filter = this.data.filter;
    this.photoURL = this.data.photoURL;
  }

  ngAfterViewInit(): void {
    const zoomContainer = this.zoomContainer.nativeElement;
    zoomContainer.onpointerdown = this.pointerDownHandler;
    zoomContainer.onpointermove = this.pointerMoveHandler;
    zoomContainer.onpointerup = this.pointerUpHandler;
    zoomContainer.onpointercancel = this.pointerUpHandler;
    zoomContainer.onpointerout = this.pointerUpHandler;
    zoomContainer.onpointerleave = this.pointerUpHandler;
    zoomContainer.onwheel = this.wheelHandler;
  }

  private pointerDownHandler = (e: PointerEvent) => {
    e.preventDefault();
    this.pointerEventCache.push(e);
    if (this.pointerEventCache.length < 2) {
      this.pinchDiff = 0;
    }
  };

  private pointerUpHandler = (e: PointerEvent) => {
    e.preventDefault();
    const index = this.pointerEventCache.findIndex((pointer) => pointer.pointerId === e.pointerId);
    this.pointerEventCache.splice(index, 1);
  };

  private pointerMoveHandler = (e: PointerEvent) => {
    e.preventDefault();

    // Check for panning
    if (this.pointerEventCache.length === 1) {
      this.translatePoint.x += e.movementX;
      this.translatePoint.y += e.movementY;
      this.setTransform();
    }

    const index = this.pointerEventCache.findIndex((pointer) => pointer.pointerId === e.pointerId);
    this.pointerEventCache[index] = e;

    // Check for pinch zooming
    if (this.pointerEventCache.length === 2) {
      const dx = this.pointerEventCache[0].clientX - this.pointerEventCache[1].clientX;
      const dy = this.pointerEventCache[0].clientY - this.pointerEventCache[1].clientY;
      const currDiff = Math.sqrt(dx * dx + dy * dy);
      if (this.pinchDiff === 0) {
        this.pinchDiff = currDiff;
        return;
      }
      const scale = (currDiff / this.pinchDiff) ** 2;
      const zoomScale = this.zoomScale * scale;
      this.pinchDiff = currDiff;
      const offsetX = (this.pointerEventCache[0].offsetX + this.pointerEventCache[1].offsetX) / 2;
      const offsetY = (this.pointerEventCache[0].offsetY + this.pointerEventCache[1].offsetY) / 2;
      this.setZoom(offsetX, offsetY, zoomScale);
    }
  };

  private wheelHandler = (e: WheelEvent) => {
    e.preventDefault();
    const zoomScale = e.deltaY > 0 ? this.zoomScale / 1.2 : this.zoomScale * 1.2;
    this.setZoom(e.offsetX, e.offsetY, zoomScale);
  };

  private setZoom(offsetX: number, offsetY: number, zoomScale: number) {
    const dx = (offsetX - this.translatePoint.x) / this.zoomScale;
    const dy = (offsetY - this.translatePoint.y) / this.zoomScale;
    if (zoomScale > 10) {
      return;
    }
    if (zoomScale < 1) {
      this.zoomScale = 1;
      const x = this.translatePoint.x / 1.2;
      const y = this.translatePoint.y / 1.2;
      this.translatePoint = {
        x: Math.abs(x) < 1 ? 0 : x,
        y: Math.abs(y) < 1 ? 0 : y,
      };
    } else {
      this.zoomScale = Math.max(1, zoomScale);
      this.translatePoint = {
        x: offsetX - dx * this.zoomScale,
        y: offsetY - dy * this.zoomScale,
      };
    }
    this.setTransform();
  }

  private setTransform() {
    this.zoomContainer.nativeElement.style.transform = `translate(${this.translatePoint.x}px, ${this.translatePoint.y}px) scale(${this.zoomScale})`;
  }

  incrementPhoto(increment: 1 | -1) {
    this.loading = true;
    this.filter?.incrementSelectedPhoto$.next(increment);
  }

  public closeLightBox() {
    this.dialogRef.close();
  }
}
